// ============================================
// pages/gunny-bags/CreatePOForm.tsx — Gunny PO create/edit with line items (real API)
// ============================================
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, CheckCircle, XCircle, FileText, Plus, Trash2 } from 'lucide-react';
import { seasonsAPI, gunnySuppliersAPI, districtsAPI, gunnyPosAPI, commoditiesAPI, gunnySupplierRatesAPI, godownsAPI, pacsAPI, gunnyRequestsAPI } from '../../api/services';
import { InvoiceUploadCard } from '../../components/InvoiceUploadCard';
import { formatAmount, num } from '../../types/markfed';
import type { Season, District } from '../../types/markfed';
import { BAG_TYPE_OPTIONS, DEFAULT_BAG_TYPE } from '../../components/gunny/bagTypes';

interface POItem {
  id?: number;
  bag_type: string;
  specification: string;
  quantity: number;
  rate: number;
  commodity_id?: number | null;
  delivery_godown_id?: number | null;
  pacs_id?: number | null;
}

const emptyItem: POItem = { bag_type: DEFAULT_BAG_TYPE, specification: '', quantity: 0, rate: 0, commodity_id: null, delivery_godown_id: null, pacs_id: null };

const emptyPO = {
  season_id: 0,
  supplier_id: 0,
  delivery_district_id: 0,
  po_number: '',
  po_date: new Date().toISOString().slice(0, 10),
  delivery_date: '',
  delivery_location: '',
  gst_rate: 5,
  advance_pct: 0,
  remarks: '',
};

export const CreatePOForm: React.FC = () => {
  const { id: idParam } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isCreate = !idParam || idParam === 'new' || idParam === 'create';
  const recordId = isCreate ? null : parseInt(idParam);

  const [seasons, setSeasons] = useState<Season[]>([]);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [commodities, setCommodities] = useState<any[]>([]);
  const [godowns, setGodowns] = useState<any[]>([]);
  const [pacsList, setPacsList] = useState<any[]>([]);
  // bag_type → ₹/bag from gunny_supplier_rates for the picked supplier.
  const [supplierRates, setSupplierRates] = useState<Record<string, number>>({});
  const [form, setForm] = useState<any>(emptyPO);
  const [items, setItems] = useState<POItem[]>([{ ...emptyItem }]);
  const [status, setStatus] = useState('draft');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const isLocked = ['submitted', 'dm_verified', 'manager_approved', 'fulfilled', 'closed'].includes(status);

  useEffect(() => {
    (async () => {
      try {
        const [s, sup, d, com] = await Promise.all([
          seasonsAPI.list(),
          gunnySuppliersAPI.list(),
          districtsAPI.list(),
          commoditiesAPI.list().catch(() => ({ data: [] as any[] })),
        ]);
        const list: Season[] = Array.isArray(s.data) ? s.data : [];
        setSeasons(list);
        // Hide inactive entries from NEW PO selectors — they shouldn't be
        // pickable. Inactive ones still render correctly when loading an
        // existing PO that was created against them (form keeps the saved id).
        setSuppliers((Array.isArray(sup.data) ? sup.data : []).filter((s: any) => s.is_active !== false));
        setDistricts(d.data);
        setCommodities((Array.isArray(com.data) ? com.data : []).filter((c: any) => c.is_active !== false));
        const active = list.find((x) => x.is_active) || list[0];
        if (isCreate) {
          setForm((f: any) => ({ ...f, season_id: active?.id || 0 }));
        } else if (recordId && active) {
          const res = await gunnyPosAPI.get(active.id, recordId);
          const raw = res.data as any;
          setForm({ ...emptyPO, ...raw });
          setItems(Array.isArray(raw.items) && raw.items.length > 0 ? raw.items : [{ ...emptyItem }]);
          setStatus(raw.status || 'draft');
        }
      } catch (err: any) {
        setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to load' });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-fill PO Number using the same generator the bundle path uses:
  // DIST3-PO-YYYYMMDD-NNN. Fires only on create, only when the field is
  // blank, and only after both delivery_district and po_date are set.
  useEffect(() => {
    if (!isCreate) return;
    if (form.po_number) return;
    if (!form.delivery_district_id || !form.po_date || !form.season_id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await gunnyRequestsAPI.poNumberPreview(
          form.season_id, form.delivery_district_id, form.po_date,
        );
        if (cancelled) return;
        if (data?.po_number) setForm((f: any) => ({ ...f, po_number: data.po_number }));
      } catch { /* let backend auto-generate at save time as a fallback */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.delivery_district_id, form.po_date, form.season_id]);

  // Reload godowns + PACS whenever the delivery district changes. Both
  // dropdowns scope to the chosen district.
  useEffect(() => {
    if (!form.delivery_district_id) { setGodowns([]); setPacsList([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const [g, p] = await Promise.all([
          godownsAPI.list(form.delivery_district_id),
          pacsAPI.list(form.delivery_district_id),
        ]);
        if (cancelled) return;
        setGodowns((Array.isArray(g.data) ? g.data : []).filter((x: any) => x.is_active));
        setPacsList((Array.isArray(p.data) ? p.data : []).filter((x: any) => x.is_active));
      } catch {
        if (!cancelled) { setGodowns([]); setPacsList([]); }
      }
    })();
    return () => { cancelled = true; };
  }, [form.delivery_district_id]);

  // Pull the supplier's rate sheet whenever the supplier changes. Used to
  // auto-fill the rate column when a line item's bag_type changes.
  useEffect(() => {
    if (!form.supplier_id) { setSupplierRates({}); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await gunnySupplierRatesAPI.list(form.supplier_id);
        if (cancelled) return;
        const map: Record<string, number> = {};
        (data || []).forEach((r: any) => { if (r.is_active) map[r.bag_type] = Number(r.rate_per_bag); });
        setSupplierRates(map);
      } catch {
        if (!cancelled) setSupplierRates({});
      }
    })();
    return () => { cancelled = true; };
  }, [form.supplier_id]);

  const totals = useMemo(() => {
    let bags = 0, subtotal = 0;
    for (const it of items) {
      bags += num(it.quantity);
      subtotal += num(it.quantity) * num(it.rate);
    }
    subtotal = +subtotal.toFixed(2);
    const gst = +((subtotal * num(form.gst_rate)) / 100).toFixed(2);
    const total = subtotal + gst;
    const advance = +((total * num(form.advance_pct)) / 100).toFixed(2);
    return { bags, subtotal, gst, total, advance };
  }, [items, form.gst_rate, form.advance_pct]);

  const handleChange = (field: string, value: any) => setForm((f: any) => ({ ...f, [field]: value }));

  const updateItem = (idx: number, field: keyof POItem, value: any) => {
    setItems((prev) => prev.map((it, i) => {
      if (i !== idx) return it;
      const next = { ...it, [field]: value };
      // Master rate is enforced — override the row rate whenever bag_type
      // changes. Backend ignores anything else; this keeps the totals box
      // honest and the operator sees the right amount immediately.
      if (field === 'bag_type') {
        const master = supplierRates[String(value)];
        next.rate = master != null ? master : 0;
      }
      return next;
    }));
  };

  // Refresh every row's rate when the supplier rate sheet finishes loading
  // (or when the supplier changes). Otherwise rows added before the supplier
  // was picked stay at rate 0.
  useEffect(() => {
    setItems((prev) => prev.map((it) => {
      const master = supplierRates[String(it.bag_type)];
      return master != null ? { ...it, rate: master } : it;
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierRates]);
  const addItem = () => setItems((prev) => [...prev, { ...emptyItem }]);
  const removeItem = (idx: number) => setItems((prev) => prev.filter((_, i) => i !== idx));

  const handleSave = async () => {
    if (!form.season_id || !form.supplier_id || !form.po_number || !form.po_date) {
      setMessage({ type: 'error', text: 'Season, supplier, PO number and date are required' });
      return;
    }
    const validItems = items.filter((it) => it.quantity > 0 && it.rate > 0);
    if (validItems.length === 0) {
      setMessage({ type: 'error', text: 'At least one line item with quantity and rate is required' });
      return;
    }
    // Master-rate enforcement: every line item's bag_type must have a rate
    // configured for the picked supplier.
    const missing = items
      .filter((it) => it.quantity > 0)
      .filter((it) => supplierRates[String(it.bag_type)] == null)
      .map((it) => it.bag_type);
    if (missing.length > 0) {
      setMessage({ type: 'error', text: `No rate in supplier master for bag type(s): ${Array.from(new Set(missing)).join(', ')}. Add them in the supplier rate master first.` });
      return;
    }
    setSaving(true);
    setMessage(null);
    try {
      const seasonId = form.season_id;
      // Each item carries its own commodity, godown, and PACS. PO-level
      // commodity rolls up from items on the backend (single value when items
      // agree, NULL when mixed) — no separate PO-level field on this form.
      const itemsResolved = validItems.map((it) => ({
        ...it,
        commodity_id: (it as any).commodity_id || null,
        delivery_godown_id: (it as any).delivery_godown_id || null,
        pacs_id: (it as any).pacs_id || null,
      }));
      const payload = { ...form, items: itemsResolved };
      if (isCreate) {
        const res = await gunnyPosAPI.create(seasonId, payload);
        const newId = (res.data as any)?.id;
        setMessage({ type: 'success', text: 'PO saved' });
        if (newId) navigate(`/gunny-bags/procurement/edit/${newId}`, { replace: true });
      } else if (recordId) {
        await gunnyPosAPI.update(seasonId, recordId, payload);
        setMessage({ type: 'success', text: 'Updated' });
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.message || 'Failed to save' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="max-w-5xl mx-auto flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-amber-600 animate-spin" /><span className="ml-2 text-gray-500">Loading...</span></div>;
  }

  return (
    <div className="max-w-5xl mx-auto">
      <button onClick={() => navigate('/gunny-bags/procurement')}
        className="mb-4 flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg">
        <ArrowLeft className="w-4 h-4" />Back to List
      </button>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
          <FileText className="w-7 h-7 text-amber-600" />
          {isCreate ? 'New Gunny PO' : `PO #${form.po_number || recordId}`}
        </h1>
        <p className="text-sm text-gray-500 mt-1">Status: <span className="font-semibold uppercase">{status}</span></p>
      </div>

      {message && (
        <div className={`mb-4 flex items-center gap-2 p-3 rounded-lg text-sm ${
          message.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'
        }`}>
          {message.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
          {message.text}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="mb-3 text-sm font-semibold text-gray-700 border-b pb-2">PO Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Season</label>
            <select value={form.season_id} onChange={(e) => handleChange('season_id', parseInt(e.target.value))}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50">
              <option value={0}>-- Select --</option>
              {seasons.map((s) => (<option key={s.id} value={s.id}>{s.season_name}</option>))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Supplier</label>
            <select value={form.supplier_id} onChange={(e) => handleChange('supplier_id', parseInt(e.target.value))}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50">
              <option value={0}>-- Select --</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>{s.supplier_name} {s.tender_id ? `(${s.tender_id})` : ''}</option>
              ))}
            </select>
            {form.supplier_id > 0 && (
              <p className="text-[11px] text-gray-500 mt-1">
                Rate sheet: {Object.keys(supplierRates).length === 0 ? <span className="text-amber-700">no rates configured — set in supplier master</span> : `${Object.keys(supplierRates).length} bag-type(s) on file`}
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">PO Number</label>
            <input type="text" value={form.po_number || ''} onChange={(e) => handleChange('po_number', e.target.value)}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">PO Date</label>
            <input type="date" value={form.po_date || ''} onChange={(e) => handleChange('po_date', e.target.value)}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Delivery Date</label>
            <input type="date" value={form.delivery_date || ''} onChange={(e) => handleChange('delivery_date', e.target.value)}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Delivery District</label>
            <select value={form.delivery_district_id || 0} onChange={(e) => handleChange('delivery_district_id', parseInt(e.target.value))}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50">
              <option value={0}>-- Select --</option>
              {districts.map((d) => (<option key={d.id} value={d.id}>{d.name}</option>))}
            </select>
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-medium text-gray-700 mb-1">Notes (optional)</label>
            <input type="text" value={form.delivery_location || ''} onChange={(e) => handleChange('delivery_location', e.target.value)}
              disabled={isLocked}
              placeholder="e.g. unloading bay 3, gate-pass instructions — godowns are picked per line item below"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50" />
          </div>
        </div>

        <div className="mt-6 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700">Line Items</h3>
          {!isLocked && (
            <button onClick={addItem} className="flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded">
              <Plus className="w-3 h-3" />Add Item
            </button>
          )}
        </div>
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 text-xs">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-2 py-2 text-left font-semibold text-gray-600 uppercase">PACS</th>
                <th className="px-2 py-2 text-left font-semibold text-gray-600 uppercase">Commodity</th>
                <th className="px-2 py-2 text-left font-semibold text-gray-600 uppercase">Type</th>
                <th className="px-2 py-2 text-left font-semibold text-gray-600 uppercase">Specification</th>
                <th className="px-2 py-2 text-left font-semibold text-gray-600 uppercase">Godown</th>
                <th className="px-2 py-2 text-right font-semibold text-gray-600 uppercase">Quantity</th>
                <th className="px-2 py-2 text-right font-semibold text-gray-600 uppercase">Rate</th>
                <th className="px-2 py-2 text-right font-semibold text-gray-600 uppercase">Amount</th>
                {!isLocked && <th className="px-2 py-2"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {items.map((it, i) => (
                <tr key={i}>
                  <td className="px-2 py-1">
                    <select value={it.pacs_id || 0}
                      onChange={(e) => updateItem(i, 'pacs_id', parseInt(e.target.value) || null)}
                      disabled={isLocked || pacsList.length === 0}
                      className="w-full px-2 py-1 border border-gray-200 rounded text-xs bg-white disabled:bg-gray-50">
                      <option value={0}>{pacsList.length === 0 ? 'pick district' : '— allot later —'}</option>
                      {pacsList.map((p: any) => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-2 py-1">
                    <select value={it.commodity_id || 0}
                      onChange={(e) => updateItem(i, 'commodity_id', parseInt(e.target.value) || null)}
                      disabled={isLocked}
                      className="w-full px-2 py-1 border border-gray-200 rounded text-xs bg-white disabled:bg-gray-50">
                      <option value={0}>—</option>
                      {commodities.map((c: any) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-2 py-1">
                    <select value={it.bag_type} onChange={(e) => updateItem(i, 'bag_type', e.target.value)}
                      disabled={isLocked}
                      className="w-full px-2 py-1 border border-gray-200 rounded text-xs disabled:bg-gray-50">
                      {BAG_TYPE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-2 py-1">
                    <input type="text" value={it.specification} onChange={(e) => updateItem(i, 'specification', e.target.value)}
                      disabled={isLocked}
                      placeholder="e.g., 50 kg, new"
                      className="w-full px-2 py-1 border border-gray-200 rounded text-xs disabled:bg-gray-50" />
                  </td>
                  <td className="px-2 py-1">
                    <select value={it.delivery_godown_id || 0}
                      onChange={(e) => updateItem(i, 'delivery_godown_id', parseInt(e.target.value) || null)}
                      disabled={isLocked || godowns.length === 0}
                      className="w-full px-2 py-1 border border-gray-200 rounded text-xs bg-white disabled:bg-gray-50">
                      <option value={0}>{godowns.length === 0 ? 'pick district' : '—'}</option>
                      {godowns.map((g: any) => (
                        <option key={g.id} value={g.id}>{g.godown_name}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-2 py-1">
                    <input type="number" value={it.quantity || ''} onChange={(e) => updateItem(i, 'quantity', parseInt(e.target.value) || 0)}
                      disabled={isLocked}
                      className="w-full px-2 py-1 border border-gray-200 rounded text-xs text-right disabled:bg-gray-50" />
                  </td>
                  <td className="px-2 py-1">
                    {(() => {
                      const master = supplierRates[it.bag_type];
                      if (master == null) {
                        return (
                          <div className="text-right">
                            <span className="text-rose-600 text-xs font-medium">missing</span>
                            <div className="text-[9px] text-rose-500 mt-0.5">add rate to master</div>
                          </div>
                        );
                      }
                      return (
                        <div className="text-right tabular-nums text-gray-800">
                          ₹ {master.toFixed(2)}
                          <div className="text-[9px] text-gray-400 mt-0.5">from master</div>
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-2 py-1 text-right font-medium">{formatAmount(+(it.quantity * it.rate).toFixed(2))}</td>
                  {!isLocked && (
                    <td className="px-2 py-1 text-center">
                      {items.length > 1 && (
                        <button onClick={() => removeItem(i)} className="p-1 text-red-600 hover:bg-red-50 rounded">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">GST %</label>
            <input type="number" step="0.01" value={form.gst_rate || ''} onChange={(e) => handleChange('gst_rate', parseFloat(e.target.value) || 0)}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-right disabled:bg-gray-50" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Advance %</label>
            <input type="number" step="0.01" value={form.advance_pct || ''} onChange={(e) => handleChange('advance_pct', parseFloat(e.target.value) || 0)}
              disabled={isLocked}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-right disabled:bg-gray-50" />
          </div>
        </div>

        <div className="mt-6 p-4 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg border border-amber-200">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
            <div><p className="text-xs text-gray-500">Total Bags</p><p className="text-base font-bold">{totals.bags.toLocaleString('en-IN')}</p></div>
            <div><p className="text-xs text-gray-500">Subtotal</p><p className="text-base font-bold">{formatAmount(totals.subtotal)}</p></div>
            <div><p className="text-xs text-gray-500">GST</p><p className="text-base font-bold">{formatAmount(totals.gst)}</p></div>
            <div><p className="text-xs text-gray-500">Total Amount</p><p className="text-base font-bold text-amber-700">{formatAmount(totals.total)}</p></div>
            <div><p className="text-xs text-gray-500">Advance</p><p className="text-base font-bold">{formatAmount(totals.advance)}</p></div>
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-xs font-medium text-gray-700 mb-1">Remarks</label>
          <textarea value={form.remarks || ''} onChange={(e) => handleChange('remarks', e.target.value)}
            disabled={isLocked} rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm disabled:bg-gray-50" />
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button onClick={handleSave} disabled={saving || isLocked}
            className="flex items-center gap-2 px-5 py-2.5 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-lg disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isCreate ? 'Save Draft' : 'Update'}
          </button>
        </div>
      </div>

      {!isCreate && recordId && form.season_id && (
        <InvoiceUploadCard
          seasonId={form.season_id}
          recordId={recordId}
          api={gunnyPosAPI}
          record={form}
          locked={isLocked}
          label="Supplier Invoice"
          description="Upload the gunny supplier's invoice (PDF, PNG, or JPG — max 10 MB)."
          onChange={(u) => setForm((f: any) => ({ ...f, ...u }))}
        />
      )}
    </div>
  );
};

export default CreatePOForm;
